<template>
    <div class="main">
        <section class="admin-section">
            <div class="container">
                <div class="row">
                    <h1 class="section-heading admin-table-heading col-12">
                        Edit "Fleet"
                    </h1>
                    <label for="filter-select">Filter by category:</label>
                    <b-form-select id="filter-select" v-model="selectedCategory" :options="vesselsCategories"
                        class="filter-select">
                        <template #first>
                            <b-form-select-option :value="null">-- Filter vessels by category --</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-table id="fleet-table" :busy="loading" :items="items" :fields="fields" :per-page="perPage"
                        :current-page="currentPage" :filter-function="filterByCategory" :filter="selectedCategory"
                        @filtered="onFiltered" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" sort-icon-left>

                        <template #cell(commands)="row">
                            <b-button size="sm" @click="deleteItem(row.item)" class="mr-2 delete-button">
                                Delete
                            </b-button>
                            <b-button size="sm" @click="editItem(row.item)" class="mr-2 edit-button">
                                Edit
                            </b-button>
                        </template>

                        <template #table-busy>
                            <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle"></b-spinner>
                                <strong>Loading...</strong>
                            </div>
                        </template>
                    </b-table>
                </div>
                <div class="row">
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"
                        aria-controls="fleet-table" class="col-7">
                    </b-pagination>
                    <div class="col-5">
                        <b-button @click="onAddNew" class="float-right submit-button">Add new vessel</b-button>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12">
                        <b-button @click="onReplaceAll" class="float-right replace-button">Replace all vessels
                            data</b-button>
                    </div>
                </div>
            </div>
        </section>

        <b-modal ref="editPopup" :no-close-on-backdrop="true" title="Edit/Create vessel data" @close="onPopupClose"
            @ok="onEditSave" @cancel="onPopupCancel" size="lg">
            <b-overlay :show="updating">
                <form v-if="item">
                    <b-form-group label="Vessel category:" label-for="category"
                        invalid-feedback="Vessel category must be selected." :state="categoryState" label-cols="2">
                        <b-form-select id="category" v-model="item.category" :options="vesselsCategories"
                            :state="categoryState">
                        </b-form-select>
                    </b-form-group>
                    <b-form-group label="Type:" label-for="type" invalid-feedback="Enter at least 2 letters."
                        :state="typeState" label-cols="2">
                        <b-form-input id="type" v-model="item.type" required :state="typeState" trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="Count:" label-for="count" invalid-feedback="Count should be greater than 1."
                        :state="countState" label-cols="2">
                        <b-form-input id="count" v-model="item.count" required :state="countState" type="number"
                            trim></b-form-input>
                    </b-form-group>
                </form>
            </b-overlay>
        </b-modal>

        <b-modal ref="confirmDelete" :no-close-on-backdrop="true" title="Delete vessel group from list"
            @close="onPopupClose" @ok="onConfirmDelete" @cancel="onPopupCancel">
            <p v-if="item">
                Are you sure you want to delete
                <strong>"{{ item.category }} {{ item.type }}"</strong> vessels from
                database?
            </p>
        </b-modal>

        <b-modal ref="replaceAll" :no-close-on-backdrop="true" title="Replace all vessels in the list"
            @close="onPopupClose" @ok="onReplaceInitiated" @cancel="onPopupCancel">
            <p class="danger">!!!IMPORTANT!!!</p>
            <p class="danger">This action will replace all existing data in the list!</p>
            <p><input type="file" ref="fileInput"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
            </p>
            <p><i>Select an XLSX file containing only one sheet of vessels data.</i></p>
            <p><i>Data must be presented in three columns:</i></p>
            <ul>
                <li><i>'category' (e.g. 'Cape'),</i></li>
                <li><i>'type' (e.g. 'Standard Newcastlemax'),</i></li>
                <li><i>'count' (e.g. '5').</i></li>
            </ul>
            <p><i>The table must also have a header row containing the above titles ('category', 'type', and
                    'count').</i></p>
        </b-modal>

        <b-modal ref="confirmReplace" :no-close-on-backdrop="true" title="Replace all vessels confirmation"
            @close="onPopupClose" @ok="onConfirmReplace" @cancel="onPopupCancel">
            Are you sure you want to replace all vessels data?
        </b-modal>
    </div>
</template>

<script>
import { doc, deleteDoc, updateDoc, setDoc, writeBatch } from "firebase/firestore";
import { auth, db } from "../../firebase";
import readXlsxFile from "read-excel-file";
import store from "../../store";

export default {
    category: "EditFleetByType",
    data() {
        return {
            updating: false,
            inEditing: false,
            vesselsCategories: [
                "Cape",
                "Babycape and Postpanamax",
                "Panamax",
                "Ultramax",
            ],
            perPage: 20,
            currentPage: 1,
            fields: [
                { key: "category", sortable: true },
                { key: "type", sortable: true },
                { key: "count", sortable: true },
                "commands",
            ],
            selectedCategory: null,
            sortBy: "",
            sortDesc: false,
            item: null,
            cachedItem: null,
            editPopup: null,
            confirmDelete: null,
        };
    },
    computed: {
        loading() {
            return this.inEditing || !this.items || this.items.length === 0;
        },
        items() {
            return this.$store.state.vesselsByType;
        },
        rows() {
            if (this.items) {
                return this.items.length;
            } else {
                return 0;
            }
        },
        categoryState() {
            return this.item.category.length > 1 ? true : false;
        },
        typeState() {
            return this.item.type.length > 1 ? true : false;
        },
        countState() {
            return this.item.count > 0 ? true : false;
        },
    },
    methods: {
        onReplaceAll() {
            this.replaceAll.show();
        },
        onReplaceInitiated(e) {
            e.preventDefault();

            if (this.$refs.fileInput.files && this.$refs.fileInput.files[0]) {
                this.confirmReplace.show();
            } else {
                alert('Select an Excel file to replace vessels data.')
            }
        },
        async onConfirmReplace() {
            const schema = {
                category: {
                    prop: "category",
                    type: String,
                },
                type: {
                    prop: "type",
                    type: String,
                },
                count: {
                    prop: "count",
                    type: Number,
                },
            };

            try {
                this.inEditing = true;

                const { rows, errors } = await readXlsxFile(
                    this.$refs.fileInput.files[0],
                    {
                        schema,
                        ignoreEmptyRows: false,
                        transformData(data) {
                            if (data[0] && data[0].length) {

                                for (let i = 0; i < data[0].length; i++) {
                                    data[0][i] = data[0][i].toLowerCase();
                                }
                            }

                            return data;
                        }
                    }
                );

                if (!errors || errors.length === 0) {
                    if (rows.filter((r) => !!r).length > 0) {
                        this.replaceAll.hide();

                        const batch = writeBatch(db);

                        this.items.forEach((current) => {
                            batch.delete(doc(db, "vesselsByType", current.id))
                        })

                        for (const row of rows) {
                            var newRef = doc(db, "vesselsByType", row.type);
                            batch.set(newRef, row);
                        }

                        await batch.commit();

                        await store.dispatch('getVesselsByType');

                        alert("New vessels data successfully uploaded.");
                    } else {
                        alert("Provided data does not fit Vessels schema.");
                    }
                } else {
                    alert("Some errors ocurred white reading Excel data.");
                }
            } catch {
                alert("Uploading new Vessels data failed.");
            } finally {
                this.inEditing = false;
            }
        },
        filterByCategory(item, currentCategory) {
            return item.category === currentCategory;
        },
        deleteItem(item) {
            if (item && this.confirmDelete) {
                this.item = item;
                this.confirmDelete.show();
            }
        },
        editItem(item) {
            if (item && this.editPopup) {
                this.item = JSON.parse(JSON.stringify(item));
                this.editPopup.show();
            }
        },
        async onConfirmDelete() {
            let index;

            if (this.item) {
                try {
                    this.inEditing = true;
                    await deleteDoc(doc(db, "vesselsByType", this.item.id));

                    index = this.items.findIndex((item) => item.id === this.item.id);
                    this.items.splice(index, 1);

                    alert(
                        this.item.category + " " + this.item.type + " successfully deleted."
                    );
                    this.item = null;
                } catch {
                    alert("Vessel delete failed.");
                } finally {
                    this.inEditing = false;
                }
            }
        },
        async onEditSave(e) {
            e.preventDefault();

            if (
                this.item &&
                this.categoryState &&
                this.countState &&
                this.countState
            ) {
                let id = this.item.id,
                    docRef,
                    index,
                    clone;

                this.updating = true;

                this.item.count = Number(this.item.count);

                if (id) {
                    // update item
                    try {
                        clone = Object.assign({}, this.item);
                        delete clone.id;

                        docRef = doc(db, "vesselsByType", id);
                        await updateDoc(docRef, clone);

                        index = this.items.findIndex((item) => item.id === id);
                        this.$set(this.items, index, JSON.parse(JSON.stringify(this.item)));

                        alert(
                            this.item.category +
                            " " +
                            this.item.type +
                            " successfully updated."
                        );
                        this.item = null;
                        this.editPopup.hide();
                    } catch {
                        alert("Vessel update failed.");
                    } finally {
                        this.updating = false;
                    }
                } else {
                    // create item
                    try {
                        docRef = doc(db, "vesselsByType", this.item.type);

                        await setDoc(docRef, this.item);

                        this.item.id = docRef.id;

                        this.items.push(JSON.parse(JSON.stringify(this.item)));

                        alert(
                            this.item.category +
                            " " +
                            this.item.type +
                            " successfully created."
                        );
                        this.item = null;
                        this.editPopup.hide();
                    } catch {
                        alert("Vessel create failed.");
                    } finally {
                        this.updating = false;
                    }
                }
            } else {
                alert("All data fields must be populated for each vessel.");
            }
        },
        onPopupClose() {
            this.item = null;
        },
        onPopupCancel() {
            this.item = null;
        },
        onFiltered() {
            this.currentPage = 1;
        },
        onAddNew() {
            this.editItem({
                category: "",
                type: "",
                count: 0,
            });
        },
    },
    mounted() {
        if (!auth.currentUser) {
            this.$router.push("/admin");
        }

        this.editPopup = this.$refs["editPopup"];
        this.confirmDelete = this.$refs["confirmDelete"];
        this.replaceAll = this.$refs["replaceAll"];
        this.confirmReplace = this.$refs["confirmReplace"];
    },
};
</script>

<style scoped>
.filter-select {
    margin-bottom: 1em;
}

.replace-button {
    background-color: #e72131;
    margin: 3rem 0;
}

.danger {
    color: #e72131;
    font-weight: 800;
}
</style>
